import { archiveByKey, type Archive } from '../../api/archives';
import type { Track, TransitionType, VideoClip } from '../../format/types';
import { TRANSITION_TYPES } from '../../format/types';
import { useTimeline } from '../../state/timeline';
import EffectsEditor from './EffectsEditor';
import { Num, Slider } from './fields';
import ParamSlider from './ParamSlider';

export default function VideoClipInspector({
  clip,
  track,
  archives,
  onSeek,
}: {
  clip: VideoClip;
  track: Extract<Track, { kind: 'video' }>;
  archives: Archive[];
  onSeek?: (t: number) => void;
}) {
  const edit = useTimeline((s) => s.edit);
  const update = useTimeline((s) => s.updateClip);
  const setTrans = useTimeline((s) => s.setClipTransition);
  const removeTrans = useTimeline((s) => s.removeClipTransition);
  const src = edit.sources[clip.source];
  const pandora = src?.kind === 'pandora-video' ? src : null;
  const archive = pandora ? archiveByKey(archives, pandora.archive) : undefined;

  const idx = track.clips.findIndex((c) => c.id === clip.id);
  const hasPrev = idx > 0;

  const patch = (p: Partial<VideoClip>) => update(clip.id, p);
  const patchTransform = (p: Partial<VideoClip['transform']>) =>
    update(clip.id, { transform: { ...clip.transform, ...p } });

  return (
    <>
      <div className="insp-section">
        <h3>Video clip</h3>
        {pandora && (
          <div className="insp-source">
            <div className="insp-source-title">{pandora.title || pandora.item}</div>
            <div className="insp-source-meta">
              {pandora.archive} · part {pandora.part} ·{' '}
              {archive && (
                <a href={`${archive.site}/${pandora.item}`} target="_blank" rel="noreferrer">
                  open in archive ↗
                </a>
              )}
            </div>
          </div>
        )}
        <Num label="start" value={clip.start} onChange={(v) => patch({ start: Math.max(0, v) })} />
        <Num
          label="in"
          value={clip.in}
          min={0}
          onChange={(v) => patch({ in: Math.max(0, Math.min(v, clip.out - 0.05)) })}
        />
        <Num
          label="out"
          value={clip.out}
          onChange={(v) =>
            patch({
              out: Math.max(
                clip.in + 0.05,
                Math.min(v, pandora ? pandora.duration : v),
              ),
            })
          }
        />
        <Slider
          label="speed"
          value={clip.speed}
          min={0.25}
          max={4}
          step={0.05}
          onChange={(v) => patch({ speed: v })}
        />
      </div>

      <div className="insp-section">
        <h3>Audio</h3>
        <Slider
          label="volume"
          value={clip.volume}
          min={0}
          max={2}
          step={0.05}
          onChange={(v) => patch({ volume: v })}
        />
        <label className="insp-field">
          <span>muted</span>
          <input
            type="checkbox"
            checked={clip.muted}
            onChange={(e) => patch({ muted: e.target.checked })}
          />
        </label>
      </div>

      <div className="insp-section">
        <h3>Fades</h3>
        <Num
          label="fade in"
          value={clip.fadeIn}
          min={0}
          onChange={(v) => patch({ fadeIn: Math.max(0, v) })}
        />
        <Num
          label="fade out"
          value={clip.fadeOut}
          min={0}
          onChange={(v) => patch({ fadeOut: Math.max(0, v) })}
        />
      </div>

      <div className="insp-section">
        <h3>Transform</h3>
        <ParamSlider clip={clip} path="transform.scale" param={clip.transform.scale} label="scale"
          min={0.1} max={3} step={0.01} onStatic={(v) => patchTransform({ scale: v })} onSeek={onSeek} />
        <ParamSlider clip={clip} path="transform.x" param={clip.transform.x} label="x"
          min={-1} max={1} step={0.01} onStatic={(v) => patchTransform({ x: v })} onSeek={onSeek} />
        <ParamSlider clip={clip} path="transform.y" param={clip.transform.y} label="y"
          min={-1} max={1} step={0.01} onStatic={(v) => patchTransform({ y: v })} onSeek={onSeek} />
        <ParamSlider clip={clip} path="transform.rotate" param={clip.transform.rotate} label="rotate"
          min={-180} max={180} step={1} onStatic={(v) => patchTransform({ rotate: v })} onSeek={onSeek} />
        <ParamSlider clip={clip} path="transform.opacity" param={clip.transform.opacity} label="opacity"
          min={0} max={1} step={0.01} onStatic={(v) => patchTransform({ opacity: v })} onSeek={onSeek} />
      </div>

      <EffectsEditor clip={clip} onSeek={onSeek} />

      <div className="insp-section">
        <h3>Transition in</h3>
        {clip.transitionIn ? (
          <>
            <label className="insp-field">
              <span>type</span>
              <select
                value={clip.transitionIn.type}
                onChange={(e) =>
                  setTrans(clip.id, e.target.value as TransitionType, clip.transitionIn!.duration)
                }
              >
                {TRANSITION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <Num
              label="duration"
              value={clip.transitionIn.duration}
              step={0.1}
              min={0.05}
              onChange={(v) => setTrans(clip.id, clip.transitionIn!.type, Math.max(0.05, v))}
            />
            <button className="danger" onClick={() => removeTrans(clip.id)}>
              Remove transition
            </button>
            <p className="insp-hint">transition = overlap with the previous clip</p>
          </>
        ) : hasPrev ? (
          <button onClick={() => setTrans(clip.id, 'crossfade', 1)}>
            Add crossfade from previous clip
          </button>
        ) : (
          <p className="insp-hint">first clip on the track — no previous clip</p>
        )}
      </div>
    </>
  );
}
